import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { showToast } from '../../utils/toast';
import { createReply } from '../../services/boardService';

const MAX_CONTENT = 240;

function parseNameWithTrip(nameWithTrip) {
  if (!nameWithTrip) return ['', ''];
  const match = nameWithTrip.match(/^(.+?)#(.+)$/);
  if (!match) return [nameWithTrip, ''];
  return [match[1], match[2].substring(0, 8)];
}

function BoardReplyForm({ threadId }) {
  const { currentUser } = useAuth();
  const [content, setContent] = useState('');
  const [nameInput, setNameInput] = useState(currentUser?.displayName ?? '');
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const formStyle = {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: 'var(--ch-form-bg)',
    borderTop: '2px solid var(--ch-border)',
    zIndex: 40,
    paddingBottom: 'env(safe-area-inset-bottom)',
  };

  const inputBase = {
    backgroundColor: 'var(--ch-input-bg)',
    border: '1px solid var(--ch-form-border)',
    color: 'var(--ch-text)',
    fontSize: '12px',
    padding: '3px 6px',
    outline: 'none',
  };

  if (!currentUser) {
    return (
      <div style={formStyle} id="reply-form">
        <div style={{ maxWidth: '896px', margin: '0 auto', padding: '8px 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
          <span style={{ color: 'var(--ch-muted)', fontSize: '12px' }}>
            書き込むにはログインが必要です
          </span>
          <Link
            to="/login"
            style={{
              backgroundColor: 'var(--ch-submit-bg)',
              color: 'var(--ch-submit-text)',
              fontSize: '12px',
              padding: '4px 12px',
              textDecoration: 'none',
            }}
          >
            ログイン
          </Link>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!content.trim()) return;
    setLoading(true);
    try {
      const [displayName, trip] = parseNameWithTrip(nameInput);
      await createReply({
        threadId,
        content,
        authorId: currentUser.uid,
        authorName: displayName || currentUser.displayName || '匿名ユーザー',
        authorTrip: trip,
      });
      setContent('');
      setExpanded(false);
      showToast({ message: '書き込みました', type: 'success' });
    } catch (err) {
      showToast({ message: `書き込みに失敗しました: ${err.message || 'エラー'}`, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div id="reply-form" style={formStyle}>
      {expanded ? (
        <form onSubmit={handleSubmit} style={{ maxWidth: '896px', margin: '0 auto', padding: '8px 12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
            <label style={{ color: 'var(--ch-muted)', fontSize: '12px', flexShrink: 0 }}>名前:</label>
            <input
              type="text"
              value={nameInput}
              onChange={(e) => setNameInput(e.target.value)}
              placeholder="名無しさん（例: 名前#トリップ）"
              style={{ ...inputBase, flex: 1 }}
            />
            <button
              type="button"
              onClick={() => setExpanded(false)}
              style={{ color: 'var(--ch-muted)', fontSize: '11px', cursor: 'pointer', background: 'none', border: 'none' }}
            >
              ▼閉じる
            </button>
          </div>
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            maxLength={MAX_CONTENT}
            required
            rows={3}
            placeholder="書き込む内容を入力してください"
            style={{ ...inputBase, width: '100%', resize: 'vertical', boxSizing: 'border-box' }}
            autoFocus
          />
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '4px' }}>
            <span style={{ color: 'var(--ch-muted)', fontSize: '11px' }}>
              {content.length}/{MAX_CONTENT}文字
            </span>
            <button
              type="submit"
              disabled={loading || !content.trim()}
              style={{
                backgroundColor: loading || !content.trim() ? 'var(--ch-muted)' : 'var(--ch-submit-bg)',
                color: 'var(--ch-submit-text)',
                fontSize: '12px',
                padding: '3px 16px',
                cursor: loading || !content.trim() ? 'not-allowed' : 'pointer',
                marginLeft: 'auto',
                border: 'none',
              }}
            >
              {loading ? '送信中...' : '書き込む'}
            </button>
          </div>
        </form>
      ) : (
        <div style={{ maxWidth: '896px', margin: '0 auto', padding: '8px 12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <button
            type="button"
            onClick={() => setExpanded(true)}
            style={{
              ...inputBase,
              flex: 1,
              textAlign: 'left',
              cursor: 'pointer',
            }}
          >
            返信を書き込む... （名前: {nameInput || '名無しさん'}）
          </button>
          <button
            type="button"
            onClick={() => setExpanded(true)}
            style={{
              backgroundColor: 'var(--ch-submit-bg)',
              color: 'var(--ch-submit-text)',
              fontSize: '12px',
              padding: '4px 14px',
              cursor: 'pointer',
              flexShrink: 0,
              border: 'none',
            }}
          >
            書き込む
          </button>
        </div>
      )}
    </div>
  );
}

export default BoardReplyForm;
